























import Vue from 'vue';
import { Watch } from 'vue-property-decorator';
import Component, { mixins } from 'vue-class-component';
import ParentComponentMixin from '@/common/ts/child-component/ParentComponentMixin';
import ChildComponentCollection from '@/common/ts/child-component/ChildComponentCollection';
import ResizeUpdateMixin from '@/common/ts/resizing/ResizeUpdateMixin';
import FooterSectionsItem from '@/webcomponents/footer-sections/FooterSectionsItem.vue';
import BackgroundColorMixin from '@/common/ts/background-color/BackgroundColorMixin';

const SECTION_SCROLL_HEIGHT = 70;

const FooterSectionsProps = Vue.extend({
  props: {
    editMode: Boolean,
    pageOpenedInEditMode: Boolean,
  },
});

@Component
export default class FooterSections extends mixins(FooterSectionsProps, ParentComponentMixin, ResizeUpdateMixin, BackgroundColorMixin) {
  $refs!: {
    wrapperElement: HTMLElement;
    itemPlaceholders: HTMLElement[];
  }

  items: ChildComponentCollection<FooterSectionsItem> = new ChildComponentCollection<FooterSectionsItem>();
  activeItemIndex: number | null = null;
  overflowHeight = 0;

  created () {
    this.initParentComponentMixin(
      this.items,
      ['FooterSectionsItem', 'FooterSitemap'],
      this.onItemAdded,
    );
    this.updateHeight();
    this.$on('resizeEnd', this.updateHeight);
  }

  mounted () {
    if (!this.editModeOrPageOpenedInEditMode) {
      window.addEventListener('scroll', this.handleScroll, {
        passive: true,
      });
      this.$el.addEventListener('footerSectionsItemFocused', this.onSubItemFocused);
      this.$el.addEventListener('footerSitemapItemFocused', this.onSitemapItemFocused);
    }
  }

  updated (): void {
    this.updateHeight();
  }

  onItemAdded (): void {
    this.updateHeight();
  }

  // for experience fragment in aem: edit-mode is not active for the component in the experience
  // fragment but we are currently still in the author view and have to apply the same behaviour
  // as for edit-mode
  get editModeOrPageOpenedInEditMode (): boolean {
    return this.editMode || this.pageOpenedInEditMode;
  }

  get rootClass () {
    return {
      'brandhub-footer-sections--edit-mode': this.editModeOrPageOpenedInEditMode,
    };
  }

  get rootStyle () {
    return {
      '--wrapper-height': `${(this.items.length * 100) + 50}vh`,
      '--overflow-height': `${this.overflowHeight}px`,
    };
  }

  updateHeight () {
    if (this.items) {
      const lastItem = this.items.last();
      if (lastItem) {
        // explained: a computed style of bottom = -115px means that the element is overflowing its
        // container by 115px.
        // a computed style of bottom = +15px means, that the element is not overflowing.
        const bottomStyle = getComputedStyle(lastItem.$el).bottom;
        const bottomValue = parseInt(
          bottomStyle.replace('px', ''),
          10,
        );
        this.overflowHeight = Math.max(
          -bottomValue,
          0,
        );
      }
    }
  }

  handleFocus (event: FocusEvent) {
    const target = event.target as EventTarget;
    if (this.$el.contains(target as Node)) {
      const currItem = target as HTMLElement;
      const currItemIndex = currItem.getAttribute('item-index');
      if (currItemIndex !== '') {
        const currItemIndexNumber = Number(currItemIndex);
        if (!Number.isNaN(currItemIndexNumber)) {
          this.activeItemIndex = currItemIndexNumber;
        }
      }
    }
  }

  handleScroll () {
    if (this.$refs.wrapperElement) {
      const { top } = this.$refs.wrapperElement.getBoundingClientRect();
      this.handleScrollCalculation(top, window.innerHeight);
    }
  }

  handleScrollCalculation (top: number, innerHeight: number) {
    const relevantSpace = innerHeight * (SECTION_SCROLL_HEIGHT / 80);
    const normalizedTop = Math.abs(top);
    let section: number | null = Math.floor((normalizedTop) / relevantSpace);
    const itemCount = this.items.length;
    if (section > 0 && top > 0) {
      section = null;
    }
    if (section !== null && section >= itemCount) {
      section = itemCount;
    }
    // fading items
    if (section === null) {
      this.activeItemIndex = null;
    } else if (section < itemCount) {
      const activeItem = this.items.items.find((item) => item.itemIndex === section);
      this.activeItemIndex = activeItem ? activeItem.index : null;
    }
  }

  @Watch('activeItemIndex')
  onIndexChange (newIndex: number | null, oldIndex: number | null) {
    if (newIndex !== null && oldIndex !== null) {
      if (newIndex > oldIndex) {
        this.items.items[newIndex].fadeIn();
        this.items.items[oldIndex].fadeOut(true);
      } else if (newIndex < oldIndex) {
        this.items.items[newIndex].fadeIn();
        this.items.items[oldIndex].fadeOut(false);
      }
    } else if (newIndex !== null && oldIndex === null) {
      this.items.items[newIndex].fadeIn();
    } else if (newIndex === null && oldIndex !== null) {
      this.items.items[oldIndex].fadeOut(true);
    }
  }

  onSubItemFocused (evt: Event) {
    const index = (evt as CustomEvent).detail.item.parentIndex;
    this.scrollToIndex(index, null);
  }

  onSitemapItemFocused (evt: Event) {
    const index = this.items.length - 1;
    if (index !== this.activeItemIndex) {
      this.activeItemIndex = index;
    }
    const el = (evt as CustomEvent).detail.item.$el;
    const minScrollY = window.scrollY + el.getBoundingClientRect().top
      - (window.innerHeight || document.documentElement.clientHeight) * 0.5;
    this.scrollToIndex(index, minScrollY);
  }

  scrollToIndex (index: number, minScrollY: number | null) {
    if (this.$refs.itemPlaceholders && this.$refs.itemPlaceholders[index]) {
      const scrollToElem = this.$refs.itemPlaceholders[index];
      const tolerance = (window.innerHeight || document.documentElement.clientHeight) * 0.25;
      const currentScrollY = window.scrollY;
      let newScrollY = currentScrollY + scrollToElem.getBoundingClientRect().top;
      if (minScrollY && minScrollY > newScrollY) {
        newScrollY = minScrollY;
      }
      if (Math.abs(newScrollY - currentScrollY) > tolerance) {
        window.scrollTo(0, newScrollY);
      }
    }
  }
}

